import React, { ReactNode, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, useDisclosure } from '@chakra-ui/react';
import { matchPath, useLocation } from 'react-router-dom';
import Sidebar from '../sidebar';
import Header from '../header';
import WaLayout from '../WhatsAppChat/WaLayout';
import { PrivateRoutes } from '../../routes/private.routes';
import { AuditoriaTopbar } from '../../pages/modulesHome/auditoriaInteligente/AuditoriaTopbar';
import { FinanceiroTopbar } from '../../pages/modulesHome/financeiro/FinanceiroTopbar';
import { CardAtualizacoes } from '../../pages/modulesHome/accountingConciliation/conciliacao-contabil/components/CardAtualizacoes';

const Layout = ({ children }: { children: ReactNode }) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });
  const location = useLocation();

  // Detectar se está no módulo WhatsApp
  const isWhatsAppModule = location.pathname.startsWith('/whatsapp');

  // Header é `fixed` e a altura depende do conteúdo (abas do módulo, largura,
  // zoom). O espaçador usa a altura medida; as classes de getSpacerHeight
  // ficam só até a primeira medição. Sem isso sobrava uma faixa entre o header
  // e a página (aparente nas telas glass, de fundo branco).
  const headerRef = useRef<HTMLDivElement>(null);
  const [alturaHeader, setAlturaHeader] = useState<number>();
  useLayoutEffect(() => {
    const el = headerRef.current?.firstElementChild as HTMLElement | null;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const medir = () => setAlturaHeader(Math.round(el.getBoundingClientRect().height));
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
    // o header é remontado ao entrar/sair do WhatsApp (outro ramo do layout)
  }, [isWhatsAppModule]);

  // Auditoria Inteligente: topbar de contexto (empresa/período) GLOBAL do
  // módulo, renderizada aqui (fora do {children}) — uma única instância,
  // persistente e visível em TODO o módulo (hub + submódulos), logo abaixo do
  // header. O estado (empresa/período) vive na URL e é preservado na navegação.
  const isAuditoriaRoute = location.pathname.startsWith('/modulesHome/auditoria-inteligente');

  // Financeiro: mesma estratégia — topbar global (período/franquia/busca) única,
  // renderizada aqui (fora do {children}), persistente no hub + sub-rotas.
  const isFinanceiroRoute = location.pathname.startsWith('/modulesHome/financeiro');

  // ContaCil: card flutuante de melhorias recentes, uma instância só para o
  // módulo inteiro (hub + Extratos + Conciliar + Importar + demais submódulos).
  const isContaCilRoute = location.pathname.startsWith(
    '/modulesHome/accountingConciliation/conciliacao-contabil',
  );

  // Detectar todas as sub-rotas para cálculo de altura
  const isSubRouteIntegration =
    location.pathname.startsWith('/modulesHome/integration') &&
    location.pathname !== '/modulesHome/integration';

  const isSubRouteSupervisor =
    location.pathname.startsWith('/modulesHome/supervisor') &&
    location.pathname !== '/modulesHome/supervisor';

  const isSubRoutePersonnelDepartment =
    location.pathname.startsWith('/modulesHome/personnelDepartment') &&
    location.pathname !== '/modulesHome/personnelDepartment';

  // Altura do header baseado no novo layout mobile (compasso) e tablet/desktop
  const getSpacerHeight = () => {
    if (isWhatsAppModule) return 'h-16';

    const hasSubRoute =
      isSubRouteIntegration || isSubRouteSupervisor || isSubRoutePersonnelDepartment;

    if (!hasSubRoute) {
      return 'h-16';
    }

    // Mobile: header compacto (abas agora estão no menu hambúrguer)
    // Tablet/Desktop: altura varia conforme módulo
    if (isSubRouteIntegration) {
      return 'h-16 md:h-20 lg:h-24 xl:h-20 2xl:h-20';
    }
    if (isSubRouteSupervisor) {
      return 'h-16 md:h-20 lg:h-24 xl:h-20 2xl:h-20';
    }
    if (isSubRoutePersonnelDepartment) {
      return 'h-16 md:h-20 lg:h-20 xl:h-20 2xl:h-20';
    }

    return 'h-16';
  };

  // Rotas com visual glassmorphism (sem moldura/padding do layout padrao,
  // pois elas usam o GlassPageBg internamente). Flag declarativa em
  // private.routes.ts (`isGlass: true`) — basta marcar a rota.
  // Memoizado: PrivateRoutes e estatico, so muda quando location.pathname muda.
  const isGlassRoute = useMemo(
    () =>
      PrivateRoutes.some(
        r => r.isGlass && matchPath({ path: r.path, end: false }, location.pathname),
      ),
    [location.pathname],
  );

  // Módulo WhatsApp: Header + Sidebar do Indicadores + WaLayout com barra horizontal
  if (isWhatsAppModule) {
    return (
      <div className="flex h-screen w-full flex-col bg-background">
        <div ref={headerRef}>
          <Header />
          <div
            className={alturaHeader ? undefined : getSpacerHeight()}
            style={alturaHeader ? { height: alturaHeader } : undefined}
          />
        </div>
        <div className="flex flex-1 overflow-hidden">
          <div className="hidden flex-shrink-0 lg:block">
            <Sidebar onToggle={onToggle} isOpen={isOpen} />
            <Box transition="0.5s ease" w={isOpen ? '260px' : '64px'} />
          </div>
          <div className="flex-1 overflow-hidden">
            <WaLayout>{children}</WaLayout>
          </div>
        </div>
      </div>
    );
  }

  // Layout padrão do Indicadores
  return (
    <div className={`h-full w-full ${isGlassRoute ? '' : 'bg-background'}`}>
      <div ref={headerRef}>
        <Header />
        <div
          className={alturaHeader ? undefined : getSpacerHeight()}
          style={alturaHeader ? { height: alturaHeader } : undefined}
        />
      </div>
      <div className="flex h-full">
        <div className="hidden lg:block">
          <Sidebar onToggle={onToggle} isOpen={isOpen} />
          <Box transition="0.5s ease" w={isOpen ? '260px' : '64px'} />
        </div>
        <div
          className={`w-full overflow-y-auto duration-500 ease-linear ${
            isGlassRoute ? '' : 'py-8'
          }`}
        >
          {isAuditoriaRoute && <AuditoriaTopbar />}
          {isFinanceiroRoute && <FinanceiroTopbar />}
          <div className={isGlassRoute ? '' : 'px-4 md:px-6'}>{children}</div>
          {isContaCilRoute && <CardAtualizacoes />}
        </div>
      </div>
    </div>
  );
};

export default Layout;
